import * as Yup from 'yup';

import {
  MINIMUM_REQUIRED_SENTENCE_LENGTH,
  MAXIMUM_TARGET_INSTRUCTION_LENGTH,
} from '../../../../../../constants';
import {
  BaselineMeasurementVersion,
  GOAL_DETAILS_FIELD_IDS,
  GOAL_TARGET_NAME_MAX_CHARS,
  GOAL_TARGET_NAME_MIN_CHARS,
  GoalTypes,
  TPGoalDataCollectionMethod,
  TreatmentPlanResourceVersion,
} from '../constants/general';

export const targetSchema = Yup.object().shape({
  label: Yup.string()
    .trim()
    .required()
    .min(GOAL_TARGET_NAME_MIN_CHARS)
    .max(GOAL_TARGET_NAME_MAX_CHARS),
  target_version: Yup.string().optional(),
  instruction: Yup.string()
    .trim()
    .required()
    .min(MINIMUM_REQUIRED_SENTENCE_LENGTH)
    .max(MAXIMUM_TARGET_INSTRUCTION_LENGTH),
  id: Yup.number().optional().nullable(),
  stage: Yup.string().when('id', {
    is: (id: number | null | undefined) => id !== undefined && id !== null,
    then: () => Yup.string().required(),
    otherwise: () => Yup.string().optional().nullable(),
  }),
  description: Yup.string()
    .trim()
    .when('target_version', {
      is: TreatmentPlanResourceVersion.V2,
      then: () =>
        Yup.string()
          .required()
          .min(MINIMUM_REQUIRED_SENTENCE_LENGTH)
          .max(MAXIMUM_TARGET_INSTRUCTION_LENGTH),
      otherwise: () => Yup.string().nullable().notRequired(),
    }),
});

const hypothesizedFunctionRowSchema = Yup.object()
  .shape({
    is_attention: Yup.boolean(),
    is_escape_or_avoidance: Yup.boolean(),
    is_sensory: Yup.boolean(),
    is_tangible: Yup.boolean(),
    date_selected: Yup.string().required(),
    id: Yup.number().optional(),
    date_edits_blocked: Yup.string().optional().nullable(),
  })
  .test(
    'at-least-one-true',
    'At least one checkbox in hypothesized function row must be checked',
    (obj) => Object.values(obj).some((val) => val === true),
  );

export const goalSchema = Yup.object().shape({
  id: Yup.number(),
  goal_type: Yup.string(),
  goal_version: Yup.string(),
  data_collection_method: Yup.string().required(),
  [GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION]: Yup.bool().nullable(),
  comparand_type: Yup.string().optional().nullable(),
  is_baseline_known_at_creation: Yup.boolean().optional().nullable(),
  is_current_level_known_at_creation: Yup.boolean().optional().nullable(),
  [GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID]: Yup.number().when(
    GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION,
    {
      is: true,
      then: () => Yup.number().required(),
      otherwise: () => Yup.number().nullable(),
    },
  ),
  [GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID]: Yup.number().required(),
  [GOAL_DETAILS_FIELD_IDS.GOAL_NAME]: Yup.object({
    behavior: Yup.object({
      id: Yup.number().required(),
    }),
    label: Yup.string(),
  }).required(),
  measurement_units: Yup.mixed()
    .nullable()
    .test('validate-measurement-units', 'Invalid measurement units structure', function (value) {
      const { data_collection_method, resource_versions } = this.parent;

      if (data_collection_method === TPGoalDataCollectionMethod.INSTANCES) {
        return value === null;
      } else if (data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE) {
        if (resource_versions.baseline_measurement === BaselineMeasurementVersion.V2) {
          return Yup.object({
            total_attempts: Yup.number().required(),
          }).isValidSync(value);
        } else {
          return Yup.object().nullable().isValidSync(value);
        }
      } else if (data_collection_method === TPGoalDataCollectionMethod.RATE) {
        return Yup.object({
          interval_unit: Yup.string().required(),
          interval_size: Yup.number().required(),
        }).isValidSync(value);
      } else if (data_collection_method === TPGoalDataCollectionMethod.DURATION) {
        return Yup.object({
          duration_unit: Yup.string().required(),
        }).isValidSync(value);
      }
      return false; // If none of the conditions match, return false
    }),
  resource_versions: Yup.object().required(),
  mastery_criteria: Yup.mixed().test(
    'validate-mastery-criteria',
    'Invalid mastery criteria structure',
    function (value) {
      const { data_collection_method, goal_type, goal_version, resource_versions } = this.parent;
      // Ensure mastery_criteria is an object
      if (!Yup.object().isType(value)) {
        return false;
      }

      if (data_collection_method === TPGoalDataCollectionMethod.INSTANCES) {
        if (goal_type === GoalTypes.BehaviorReduction) {
          return Yup.object({
            change_type: Yup.string().required(),
            desired_amount: Yup.number().required(),
            practice_duration_count: Yup.number().required(),
            practice_duration_unit: Yup.string().required(),
          }).isValidSync(value);
        }
        if (goal_type === GoalTypes.Caregiver) {
          return Yup.object({
            change_type: Yup.string().required(),
            desired_amount: Yup.number().required(),
            practice_duration_count: Yup.number().required(),
            practice_duration_unit: Yup.string().optional().nullable(),
          }).isValidSync(value);
        }
        if (goal_type === GoalTypes.SkillAcquisition) {
          if (goal_version === TreatmentPlanResourceVersion.V1) {
            return Yup.object({
              change_type: Yup.string().required(),
              desired_amount: Yup.number().required(),
              practice_duration_count: Yup.number().required(),
              practice_duration_unit: Yup.string().optional().nullable(),
            }).isValidSync(value);
          } else {
            return Yup.object({
              acquisition: Yup.object({
                change_type: Yup.string().required(),
                desired_amount: Yup.number().required(),
                practice_duration_count: Yup.number().required(),
                practice_duration_unit: Yup.string().optional().nullable(),
              }),
              generalization: Yup.object({
                change_type: Yup.string().required(),
                desired_amount: Yup.number().required(),
                practice_duration_count: Yup.number().required(),
                practice_duration_unit: Yup.string().optional().nullable(),
              }),
              maintenance: Yup.object({
                change_type: Yup.string().required(),
                desired_amount: Yup.number().required(),
                practice_duration_count: Yup.number().required(),
                practice_duration_unit: Yup.string().optional().nullable(),
              }),
              maintenance_frequency: Yup.string().required(),
            }).isValidSync(value);
          }
        }
        return false;
      } else if (data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE) {
        if (
          goal_type === GoalTypes.SkillAcquisition &&
          goal_version === TreatmentPlanResourceVersion.V2 &&
          resource_versions.baseline_measurement === BaselineMeasurementVersion.V2
        ) {
          return Yup.object({
            acquisition: Yup.object({
              desired_amount: Yup.number().required(),
              practice_duration_count: Yup.number().required(),
              total_attempts: Yup.number().nullable(),
            }),
            generalization: Yup.object({
              desired_amount: Yup.number().required(),
              practice_duration_count: Yup.number().required(),
              total_attempts: Yup.number().nullable(),
            }),
            maintenance: Yup.object({
              desired_amount: Yup.number().required(),
              practice_duration_count: Yup.number().required(),
              total_attempts: Yup.number().nullable(),
            }),
            maintenance_frequency: Yup.string().required(),
          }).isValidSync(value);
        } else if (
          goal_type === GoalTypes.SkillAcquisition &&
          goal_version === TreatmentPlanResourceVersion.V2 &&
          resource_versions.baseline_measurement === BaselineMeasurementVersion.V1
        ) {
          return Yup.object({
            acquisition: Yup.object({
              desired_amount: Yup.number().required(),
              practice_duration_count: Yup.number().required(),
              total_attempts: Yup.number().required(),
            }),
            generalization: Yup.object({
              desired_amount: Yup.number().required(),
              practice_duration_count: Yup.number().required(),
              total_attempts: Yup.number().required(),
            }),
            maintenance: Yup.object({
              desired_amount: Yup.number().required(),
              practice_duration_count: Yup.number().required(),
              total_attempts: Yup.number().required(),
            }),
            maintenance_frequency: Yup.string().required(),
          }).isValidSync(value);
        } else if (
          goal_type === GoalTypes.SkillAcquisition &&
          goal_version === TreatmentPlanResourceVersion.V1 &&
          resource_versions.baseline_measurement === BaselineMeasurementVersion.V2
        ) {
          return Yup.object({
            desired_amount: Yup.number().required(),
            practice_duration_count: Yup.number().required(),
            total_attempts: Yup.number().nullable(),
          }).isValidSync(value);
        } else if (
          goal_type === GoalTypes.SkillAcquisition &&
          goal_version === TreatmentPlanResourceVersion.V1 &&
          resource_versions.baseline_measurement === BaselineMeasurementVersion.V1
        ) {
          return Yup.object({
            desired_amount: Yup.number().required(),
            practice_duration_count: Yup.number().required(),
            total_attempts: Yup.number().required(),
          }).isValidSync(value);
        } else if (goal_type === GoalTypes.Caregiver) {
          return Yup.object({
            desired_amount: Yup.number().required(),
            practice_duration_count: Yup.number().required(),
          }).isValidSync(value);
        }
      } else if (data_collection_method === TPGoalDataCollectionMethod.RATE) {
        if (
          goal_type === GoalTypes.SkillAcquisition &&
          goal_version === TreatmentPlanResourceVersion.V2
        ) {
          return Yup.object({
            acquisition: Yup.object({
              change_type: Yup.string().required(),
              desired_amount: Yup.number().required(),
              practice_duration_count: Yup.number().required(),
              total_attempts: Yup.number().optional().nullable(),
            }),
            generalization: Yup.object({
              change_type: Yup.string().required(),
              desired_amount: Yup.number().required(),
              practice_duration_count: Yup.number().required(),
              total_attempts: Yup.number().optional().nullable(),
            }),
            maintenance: Yup.object({
              change_type: Yup.string().required(),
              desired_amount: Yup.number().required(),
              practice_duration_count: Yup.number().required(),
              total_attempts: Yup.number().optional().nullable(),
            }),
            maintenance_frequency: Yup.string().required(),
          }).isValidSync(value);
        } else {
          return Yup.object({
            change_type: Yup.string().required(),
            desired_amount: Yup.number().required(),
            practice_duration_count: Yup.number().required(),
            total_attempts: Yup.number().optional().nullable(),
          }).isValidSync(value);
        }
      } else if (data_collection_method === TPGoalDataCollectionMethod.DURATION) {
        if (
          goal_type === GoalTypes.SkillAcquisition &&
          goal_version === TreatmentPlanResourceVersion.V2
        ) {
          return Yup.object({
            acquisition: Yup.object({
              change_type: Yup.string().required(),
              desired_amount: Yup.number().required(),
              practice_duration_count: Yup.number().required(),
              measurement_mode: Yup.string().required(),
            }),
            generalization: Yup.object({
              change_type: Yup.string().required(),
              desired_amount: Yup.number().required(),
              practice_duration_count: Yup.number().required(),
              measurement_mode: Yup.string().required(),
            }),
            maintenance: Yup.object({
              change_type: Yup.string().required(),
              desired_amount: Yup.number().required(),
              practice_duration_count: Yup.number().required(),
              measurement_mode: Yup.string().required(),
            }),
            maintenance_frequency: Yup.string().required(),
          }).isValidSync(value);
        } else {
          return Yup.object({
            change_type: Yup.string().required(),
            desired_amount: Yup.number().required(),
            practice_duration_count: Yup.number().required(),
            measurement_mode: Yup.string().required(),
          }).isValidSync(value);
        }
      }
      return false; // If none of the conditions match, return false
    },
  ),
  linked_goals: Yup.object().nullable(),
  linked_behavior_reduction_goal_id: Yup.number().nullable(),
  linked_skill_acquisition_goal_ids: Yup.array().nullable(),
  status_at_creation: Yup.string().when('goal_version', {
    is: TreatmentPlanResourceVersion.V1,
    then: () => Yup.string().required(),
    otherwise: () => Yup.string().nullable().notRequired(),
  }),
  date_introduced: Yup.string().required(),
  baseline_at_creation: Yup.string().required(),
  current_level_at_creation: Yup.string().when('goal_version', {
    is: TreatmentPlanResourceVersion.V1,
    then: () => Yup.string().required(),
    otherwise: () => Yup.string().nullable().notRequired(),
  }),
  expected_mastery_date: Yup.string().when('goal_version', {
    is: TreatmentPlanResourceVersion.V2,
    then: () => Yup.string().required(),
    otherwise: () => Yup.string().nullable().notRequired(),
  }),
  progress: Yup.number(),
  is_active: Yup.boolean().optional(),
  is_linkable: Yup.boolean().optional(),
  targets: Yup.array().of(targetSchema).min(1),
  description: Yup.mixed()
    .nullable()
    .test('validate-description', 'Invalid description', function (value) {
      const { goal_type, goal_version } = this.parent;

      if (
        goal_version === TreatmentPlanResourceVersion.V2 ||
        goal_type === GoalTypes.SkillAcquisition ||
        goal_type === GoalTypes.Caregiver
      ) {
        return Yup.string()
          .trim()
          .required()
          .min(MINIMUM_REQUIRED_SENTENCE_LENGTH)
          .test('no-braces', "The string must not contain '{' or '}'", (value) => {
            if (!value) return false;
            return !/[{}]/.test(value); // Regex to ensure { or } are not present
          })
          .isValidSync(value);
      } else {
        return Yup.object().nullable().optional().isValidSync(value);
      }
    }),
  operational_definition: Yup.string().when('goal_type', {
    is: GoalTypes.BehaviorReduction,
    then: () => Yup.string().trim().required().min(MINIMUM_REQUIRED_SENTENCE_LENGTH),
    otherwise: () => Yup.string().nullable().notRequired(),
  }),
  objective: Yup.string().when('goal_type', {
    is: GoalTypes.BehaviorReduction,
    then: () => Yup.string().trim().required().min(MINIMUM_REQUIRED_SENTENCE_LENGTH),
    otherwise: () => Yup.string().nullable().notRequired(),
  }),
  antecedent_modifications: Yup.string().when('goal_type', {
    is: GoalTypes.BehaviorReduction,
    then: () => Yup.string().trim().required().min(MINIMUM_REQUIRED_SENTENCE_LENGTH),
    otherwise: () => Yup.string().nullable().notRequired(),
  }),
  consequence_modifications: Yup.string().when('goal_type', {
    is: GoalTypes.BehaviorReduction,
    then: () => Yup.string().trim().required().min(MINIMUM_REQUIRED_SENTENCE_LENGTH),
    otherwise: () => Yup.string().nullable().notRequired(),
  }),
  target_mastery: Yup.array().nullable(),

  hypothesized_functions: Yup.array().when('goal_type', {
    is: GoalTypes.BehaviorReduction,
    then: () =>
      Yup.array().of(hypothesizedFunctionRowSchema).min(1, 'Hypthesized function is required'),
    otherwise: () => Yup.array().nullable().notRequired(),
  }),
  reason: Yup.string().optional().nullable(),
});
